export const METHOD_ABBREV_REFERENCE = [
  {
    label: 'UPS Shipping Methods',
    methods: [
      { abbr: 'UPSG', name: 'UPS Ground', description: '1–5 business days domestic ground; reliable economy.' },
      { abbr: 'UPS1D', name: 'UPS Next Day Air', description: 'Guaranteed next-business-day delivery for urgent shipments.' },
      { abbr: 'UPS1DP', name: 'UPS Next Day Air Saver', description: 'Lower-cost overnight by end of day.' },
      { abbr: 'UPS2D', name: 'UPS 2nd Day Air', description: 'Guaranteed second-business-day delivery.' },
      { abbr: 'UPS3D', name: 'UPS 3 Day Select', description: '3-business-day guaranteed delivery (economical).' },
      { abbr: 'UPSU', name: 'UPS Worldwide Expedited', description: 'International expedited air, 2–5 days to many countries.' },
    ],
  },
  {
    label: 'FedEx Shipping Methods',
    methods: [
      { abbr: 'FEXG', name: 'FedEx Ground', description: 'Economical 1–5 business-day domestic ground.' },
      { abbr: 'FEXHD', name: 'FedEx Home Delivery', description: 'Residential-focused ground with evening/weekend options.' },
      { abbr: 'FEX1D', name: 'FedEx Priority Overnight', description: 'Next-business-day delivery with morning arrival guarantee.' },
      { abbr: 'FEX2D', name: 'FedEx 2Day', description: 'Guaranteed end-of-second-business-day service.' },
      { abbr: 'FEXES', name: 'FedEx Express Saver', description: 'Economical 3-business-day air service.' },
      { abbr: 'FEXF', name: 'FedEx Freight', description: 'Freight service for large/heavy shipments (LTL/pallet).' },
    ],
  },
  {
    label: 'USPS Shipping Methods',
    methods: [
      { abbr: 'PM', name: 'USPS Priority Mail', description: '1–3 day estimate with tracking and up to $100 insurance.' },
      { abbr: 'PME', name: 'USPS Priority Mail Express', description: 'Fastest USPS: overnight or 2-day guaranteed (some Sundays).' },
      { abbr: 'FC', name: 'USPS First Class Mail', description: 'Lightweight packages (<13 oz); 1–5 business days.' },
      { abbr: 'GND', name: 'USPS Ground Advantage', description: 'Economical ground 2–5 day delivery for heavier parcels.' },
    ],
  },
  {
    label: 'General / Freight',
    methods: [
      { abbr: 'LTL', name: 'Less Than Truckload', description: 'Freight shipping for palletized loads sharing trailer space.' },
      { abbr: 'FTL', name: 'Full Truckload', description: 'Dedicated truck for large shipments filling a trailer.' },
      { abbr: 'INTL', name: 'International Air', description: 'Generic international air (3–10 days depending on route).' },
      { abbr: 'STD', name: 'Standard Shipping', description: 'Default economical option; usually 3–7+ days.' },
      { abbr: 'EXP', name: 'Expedited Shipping', description: 'Faster-than-standard (2-day or next-day) delivery.' },
    ],
  },
];

export const normalizeAbbreviationList = (value = '') => (
  Array.from(new Set(
    (String(value || '').split(';')
      .map(part => String(part || '').trim().toUpperCase())
      .filter(Boolean))
  ))
);

const CARRIER_PREFIXES = new Set(['ups', 'fedex', 'usps']);

const normalizeForMatching = (value = '') => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

const splitIntoTokens = (value = '') => (
  String(value || '').split(' ').map(part => part.trim()).filter(Boolean)
);

const buildMethodPatterns = (name = '', abbr = '') => {
  const patterns = new Set();
  const normalizedName = normalizeForMatching(name);
  if (normalizedName) patterns.add(normalizedName);
  const normalizedAbbr = normalizeForMatching(abbr);
  if (normalizedAbbr) patterns.add(normalizedAbbr);

  if (normalizedName) {
    const tokens = splitIntoTokens(normalizedName);
    if (tokens.length > 1 && CARRIER_PREFIXES.has(tokens[0])) {
      const withoutPrefix = tokens.slice(1).join(' ').trim();
      if (withoutPrefix && withoutPrefix !== normalizedName) patterns.add(withoutPrefix);
    }
    if (tokens.length > 1) {
      const withoutCarrierWords = tokens.filter((token) => !CARRIER_PREFIXES.has(token)).join(' ').trim();
      if (withoutCarrierWords && withoutCarrierWords !== normalizedName) patterns.add(withoutCarrierWords);
    }
  }

  return Array.from(patterns).filter(Boolean);
};

const METHOD_PATTERN_LOOKUP = METHOD_ABBREV_REFERENCE.flatMap(category => (
  (category.methods || []).map((method) => ({
    abbr: method.abbr,
    patterns: buildMethodPatterns(method.name, method.abbr),
  }))
));

export function mapShippingMethodToAbbreviation(value) {
  const normalizedValue = normalizeForMatching(value);
  if (!normalizedValue) return null;
  const titleTokens = new Set(splitIntoTokens(normalizedValue));
  let bestMatch = null;

  for (const entry of METHOD_PATTERN_LOOKUP) {
    const { patterns } = entry;
    for (const pattern of patterns) {
      const patternTokens = splitIntoTokens(pattern);
      if (!patternTokens.length) continue;
      const matches = patternTokens.every(token => titleTokens.has(token));
      if (!matches) continue;
      const score = patternTokens.length;
      if (!bestMatch || score > bestMatch.score) {
        bestMatch = { abbr: entry.abbr, score };
      }
      break;
    }
  }

  return bestMatch?.abbr || null;
}
